import React, { useRef, useEffect, useState } from 'react';

const DEFAULT_BAND_COLORS = ['var(--mg-success)', 'var(--mg-warning)', 'var(--mg-danger)'];
const RING_FRACTIONS = [0.25, 0.5, 0.75, 1];
const LABEL_GUTTER = 60;
const LABEL_OFFSET = 14;
const LABEL_LINE_HEIGHT = 11;
const WRAP_AT = 15;

export interface RadarThresholds {
	good: number;
	moderate: number;
}

export interface D3RadarChartProps {
	data: Array<Record<string, any>>;
	dataKey: string;
	nameKey: string;
	// Radius comes from dataKey, band colour from colorKey — split them when the readable shape is a rescale of the real score.
	colorKey?: string;
	thresholds: RadarThresholds;
	max?: number;
	height?: number;
	colors?: string[];
	showTooltip?: boolean;
}

interface TooltipState {
	x: number;
	y: number;
	name: string;
	value: number;
}

// Two lines is enough at these label lengths: break on "&", else at a space near WRAP_AT.
const wrapLabel = (label: string): string[] => {
	const amp = label.indexOf('&');
	if (amp > 0) return [label.slice(0, amp).trim(), label.slice(amp).trim()];
	if (label.length <= WRAP_AT) return [label];
	const before = label.lastIndexOf(' ', WRAP_AT);
	const at = before > 0 ? before : label.indexOf(' ', WRAP_AT);
	return at > 0 ? [label.slice(0, at), label.slice(at + 1)] : [label];
};

export const D3RadarChart: React.FC<D3RadarChartProps> = ({
	data,
	dataKey,
	nameKey,
	colorKey,
	thresholds,
	max = 100,
	height = 300,
	colors = DEFAULT_BAND_COLORS,
	showTooltip = true,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(400);
	const [tooltip, setTooltip] = useState<TooltipState | null>(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		setWidth(el.clientWidth || 400);
		const ro = new ResizeObserver(entries => {
			setWidth(entries[0].contentRect.width);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	if (!data.length) return null;

	const cx = width / 2;
	const cy = height / 2;
	const r = Math.max(Math.min(cx, cy) - LABEL_GUTTER, 10);
	const scale = max > 0 ? max : 1;

	// Band is the absolute value against the caller's thresholds, never the data's own min/max —
	// ranking would paint every dataset's best point green and its worst red whatever the scores.
	const bandColor = (value: number) => {
		if (value >= thresholds.good) return colors[0];
		if (value >= thresholds.moderate) return colors[1];
		return colors[2];
	};

	const points = data.map((d, i) => {
		const raw = Number(d[dataKey]);
		const value = Number.isFinite(raw) ? raw : 0;
		const rawBand = Number(d[colorKey ?? dataKey]);
		const bandValue = Number.isFinite(rawBand) ? rawBand : 0;
		const ratio = Math.min(Math.max(value / scale, 0), 1);
		const angle = (i / data.length) * Math.PI * 2 - Math.PI / 2;
		return {
			name: String(d[nameKey] ?? ''),
			value,
			ax: Math.cos(angle),
			ay: Math.sin(angle),
			x: Math.cos(angle) * r * ratio,
			y: Math.sin(angle) * r * ratio,
			bandValue,
			fill: bandColor(bandValue),
		};
	});

	const polygonPoints = points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

	return (
		<div ref={containerRef} data-testid="radar-chart" style={{ position: 'relative', width: '100%' }}>
			<svg width={width} height={height} onMouseLeave={() => setTooltip(null)}>
				<g transform={`translate(${cx},${cy})`}>
					{RING_FRACTIONS.map(f => (
						<circle
							key={f}
							data-testid="radar-ring"
							cx={0}
							cy={0}
							r={r * f}
							fill="none"
							stroke="var(--mg-border)"
							strokeWidth={1}
						/>
					))}

					{points.map((p, i) => (
						<line
							key={i}
							data-testid="radar-spoke"
							x1={0}
							y1={0}
							x2={p.ax * r}
							y2={p.ay * r}
							stroke="var(--mg-border)"
							strokeWidth={1}
						/>
					))}

					<polygon
						data-testid="radar-polygon"
						points={polygonPoints}
						fill="var(--mg-primary)"
						fillOpacity={0.18}
						stroke="var(--mg-primary)"
						strokeWidth={2}
						strokeLinejoin="round"
					/>

					{points.map((p, i) => (
						<circle
							key={i}
							data-testid="radar-vertex"
							data-name={p.name}
							cx={p.x}
							cy={p.y}
							r={4}
							fill={p.fill}
							stroke="var(--mg-bg-elevated)"
							strokeWidth={1.5}
							onMouseMove={showTooltip ? e => {
								const rect = containerRef.current!.getBoundingClientRect();
								setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, name: p.name, value: p.bandValue });
							} : undefined}
							style={{ cursor: 'default' }}
						/>
					))}

					{points.map((p, i) => {
						const lx = p.ax * (r + LABEL_OFFSET);
						const ly = p.ay * (r + LABEL_OFFSET);
						const lines = wrapLabel(p.name);
						const anchor = Math.abs(p.ax) < 0.2 ? 'middle' : p.ax > 0 ? 'start' : 'end';
						return (
							<text
								key={i}
								data-testid="radar-point-label"
								x={lx}
								y={ly}
								textAnchor={anchor}
								dominantBaseline="middle"
								fontSize={10}
								fill="var(--mg-text-secondary)"
								style={{ pointerEvents: 'none', userSelect: 'none' }}
							>
								{lines.map((line, li) => (
									<tspan key={li} x={lx} dy={li === 0 ? -((lines.length - 1) * LABEL_LINE_HEIGHT) / 2 : LABEL_LINE_HEIGHT}>
										{line}
									</tspan>
								))}
							</text>
						);
					})}
				</g>
			</svg>

			{showTooltip && tooltip && (
				<div
					data-testid="radar-tooltip"
					style={{
						position: 'absolute',
						left: tooltip.x + 12,
						top: tooltip.y - 36,
						background: 'var(--mg-bg-elevated)',
						border: '1px solid var(--mg-border)',
						borderRadius: 6,
						padding: '5px 10px',
						fontSize: 12,
						pointerEvents: 'none',
						zIndex: 10,
						whiteSpace: 'nowrap',
						boxShadow: 'var(--mg-shadow-sm)',
					}}
				>
					<strong>{tooltip.name}</strong>: {tooltip.value.toLocaleString()}
				</div>
			)}
		</div>
	);
};
